/**
 * Salesperson Preview Banner
 *
 * Allows Admin/SuperAdmin users to preview analytics pages as if they were a specific salesperson.
 * Shows a prominent banner with salesperson selector when in preview mode.
 *
 * Features:
 * - Admin-only visibility
 * - Salesperson dropdown with search
 * - URL-based state (?previewAs=userId)
 * - Clear visual indicator
 * - Easy exit preview mode
 */

import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useCompany, useAuthenticatedApi } from "@x-veloper/dataslot-auth";
import { X, Eye } from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { t } from "@/shared/lib/th-translations";

interface Salesperson {
  salespersonId: string;
  salespersonName: string;
}

export function SalespersonPreviewBanner() {
  const { selectedCompany } = useCompany();
  const api = useAuthenticatedApi();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [salespersons, setSalespersons] = useState<Salesperson[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Only show for Admin/SuperAdmin users
  const userRole = selectedCompany?.role;
  const isAdmin = userRole === "Admin" || userRole === "SuperAdmin";

  // Get current preview salesperson from URL
  const previewSalespersonId = searchParams.get("previewAs");

  // Only show banner on analytics pages
  const isAnalyticsPage = location.pathname.startsWith("/analytics/");

  // Fetch salespersons list
  useEffect(() => {
    if (!isAdmin || !isAnalyticsPage) return;

    const fetchSalespersons = async () => {
      setIsLoading(true);
      try {
        // Use dedicated salespersons list endpoint (no period parameter needed)
        const response = await api.request("/api/salespersons/list");
        const data: any = await response.json();

        console.log("[SalespersonPreviewBanner] API Response:", {
          success: data.success,
          hasData: !!data.data,
          hasSalespersons: !!data.data?.salespersons,
          count: data.data?.salespersons?.length
        });

        if (data.success && data.data?.salespersons) {
          const sortedSalespersons = (data.data.salespersons as any[])
            .map((sp) => ({
              salespersonId: sp.salespersonId,
              salespersonName: sp.salespersonName || sp.salespersonId,
            }))
            .sort((a, b) =>
              a.salespersonName.localeCompare(b.salespersonName)
            );

          console.log("[SalespersonPreviewBanner] Sorted salespersons:", sortedSalespersons.length);
          setSalespersons(sortedSalespersons);
        } else {
          console.warn("[SalespersonPreviewBanner] Unexpected API response format:", data);
        }
      } catch (error) {
        console.error("[SalespersonPreviewBanner] Failed to fetch salespersons:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSalespersons();
  }, [isAdmin, isAnalyticsPage, api]);

  // Handle salesperson selection
  const handleSalespersonChange = (salespersonId: string) => {
    const newSearchParams = new URLSearchParams(searchParams);

    if (salespersonId === "all") {
      // Exit preview mode
      newSearchParams.delete("previewAs");
    } else {
      // Enter/change preview mode
      newSearchParams.set("previewAs", salespersonId);
    }

    navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
  };

  // Handle exit preview mode
  const handleExitPreview = () => {
    handleSalespersonChange("all");
  };

  // Don't render if not admin or not on analytics page
  if (!isAdmin || !isAnalyticsPage) {
    return null;
  }

  // Get current preview salesperson name
  const previewSalesperson = salespersons.find(
    sp => sp.salespersonId === previewSalespersonId
  );

  return (
    <div className="border-b bg-amber-50 border-amber-200">
      <div className="container mx-auto px-4 py-3">
        <Alert className="border-amber-300 bg-amber-100">
          <Eye className="h-4 w-4 text-amber-600" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1">
              <span className="text-sm font-medium text-amber-900">
                {previewSalespersonId ? (
                  <>
                    <strong>Preview Mode:</strong> Viewing as{" "}
                    <strong>{previewSalesperson?.salespersonName || previewSalespersonId}</strong>
                  </>
                ) : (
                  <>
                    <strong>Admin View:</strong> Preview analytics as a specific salesperson
                  </>
                )}
              </span>

              <Select
                value={previewSalespersonId || "all"}
                onValueChange={handleSalespersonChange}
                disabled={isLoading}
              >
                <SelectTrigger className="w-[300px] bg-white border-amber-300 text-sm">
                  <SelectValue placeholder={
                    isLoading
                      ? t("salespersonPreview.loading")
                      : salespersons.length > 0
                        ? t("salespersonPreview.selectPlaceholder", { count: salespersons.length })
                        : t("salespersonPreview.noSalespersons")
                  } />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <span className="font-medium">All Salespersons (Admin View)</span>
                  </SelectItem>
                  {salespersons.length > 0 && (
                    <>
                      <div className="h-px bg-gray-200 my-1" />
                      {salespersons.map((sp) => (
                        <SelectItem key={sp.salespersonId} value={sp.salespersonId}>
                          {sp.salespersonName}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {salespersons.length === 0 && !isLoading && (
                    <div className="px-2 py-4 text-sm text-gray-500 text-center">
                      No salespersons found
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            {previewSalespersonId && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExitPreview}
                className="border-amber-400 hover:bg-amber-200 text-amber-900"
              >
                <X className="h-3 w-3 mr-1" />
                Exit Preview
              </Button>
            )}
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}
